import type { FormPreviewSnapshot, FormPreviewSource } from '@/contracts/form-preview'
import {
  type FormDetailContract,
  type FormFolderContract,
  type FormFolderListResult,
  type FormUiContract,
  type ListFormsParams,
  ListFormsParamsSchema,
  type ListFormsResult,
  type QuestionGroupUiContract,
  createSyntheticMeta,
  isCustomerOnboardingForm,
} from '@/contracts/questionnaire'
import { DEFAULT_SHARE_MAX_RESPONSES } from '@/contracts/share'
import { normalizeFormPreviewSnapshot } from '@/lib/form-preview/normalize-preview-snapshot'
import {
  type PersistentFormDetailSnapshot,
  createQuestionOutline,
  loadFixture,
  normalizeFormGroupInstances,
  saveFormDetailSnapshot,
  savePatch,
} from '@/lib/mock-api/persistent-store'
import { mockWorkspaceId } from '@/lib/mock-data'
import { buildPublicFormUrl } from '@/lib/public-form-url'
import type { Form, FormStatus, LogicJump, Question } from '@/types/form'

function getFolderIds(form: Form, folders: FormFolderContract[]) {
  return folders.filter((folder) => folder.formIds.includes(form.id)).map((folder) => folder.id)
}

function toFormUiContract(form: Form): FormUiContract {
  const state = loadFixture()
  const responses = state.responses.filter((response) => response.formId === form.id)
  const completions = responses.filter((response) => response.status === 'complete').length
  const completionRate =
    responses.length === 0 ? 0 : Math.round((completions / responses.length) * 100)
  return {
    ...form,
    responseCount: responses.length,
    completionRate,
    folderIds: getFolderIds(form, state.formFolders),
    customerWorkflow: isCustomerOnboardingForm(form) ? 'customer_onboarding' : 'standard',
  }
}

function sortForms(forms: FormUiContract[], sort: 'updated_desc' | 'responses_desc' | 'title_asc') {
  return [...forms].sort((left, right) => {
    if (sort === 'title_asc') return left.title.localeCompare(right.title, 'zh-Hant')
    if (sort === 'responses_desc') return right.responseCount - left.responseCount
    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
  })
}

function paginate<T>(items: T[], page: number, limit: number) {
  const start = (page - 1) * limit
  return items.slice(start, start + limit)
}

function getPersistedForm(formId: string) {
  const state = loadFixture()
  return state.forms.find((form) => form.id === formId) ?? state.forms[0]
}

function getPersistedFormOrNull(formId: string) {
  const state = loadFixture()
  return state.forms.find((form) => form.id === formId) ?? null
}

function cloneFormDetailForDuplicate(
  sourceFormId: string,
  targetFormId: string,
  sourceDetail: PersistentFormDetailSnapshot,
): PersistentFormDetailSnapshot {
  const questionIdMap = new Map(
    sourceDetail.questions.map((question) => [question.id, crypto.randomUUID()]),
  )
  const groupIdMap = new Map(sourceDetail.groups.map((group) => [group.id, crypto.randomUUID()]))
  const questions = sourceDetail.questions.map((question, orderIndex) => {
    const nextQuestionId = questionIdMap.get(question.id) ?? crypto.randomUUID()
    return {
      ...structuredClone(question),
      id: nextQuestionId,
      formId: targetFormId,
      groupId: question.groupId ? groupIdMap.get(question.groupId) : undefined,
      orderIndex,
      choices: question.choices.map((choice, choiceIndex) => ({
        ...choice,
        id: crypto.randomUUID(),
        questionId: nextQuestionId,
        orderIndex: choiceIndex,
      })),
    }
  })
  const groups = sourceDetail.groups.map((group, orderIndex) => {
    const nextGroupId = groupIdMap.get(group.id) ?? crypto.randomUUID()
    return {
      ...structuredClone(group),
      id: nextGroupId,
      formId: targetFormId,
      sourceGroupId: group.sourceGroupId ?? group.id,
      orderIndex,
      questionIds: group.questionIds.map(
        (questionId) => questionIdMap.get(questionId) ?? questionId,
      ),
      questions: group.questions?.map((question) => ({
        ...structuredClone(question),
        id: questionIdMap.get(question.id) ?? question.id,
        formId: targetFormId,
        groupId: nextGroupId,
      })),
      logicJumps: group.logicJumps?.map((jump, order) =>
        cloneLogicJump(jump, questionIdMap, groupIdMap, order),
      ),
    }
  })
  const logicJumps = sourceDetail.logicJumps.map((jump, order) =>
    cloneLogicJump(jump, questionIdMap, groupIdMap, order),
  )
  const outline = sourceDetail.outline.map((item) =>
    item.kind === 'question'
      ? {
          kind: 'question' as const,
          questionId: questionIdMap.get(item.questionId) ?? item.questionId,
          groupInstanceId: null,
        }
      : {
          kind: 'group' as const,
          groupInstanceId: groupIdMap.get(item.groupInstanceId) ?? item.groupInstanceId,
        },
  )

  return {
    questions,
    logicJumps,
    hiddenFields: structuredClone(sourceDetail.hiddenFields),
    groups,
    outline,
  }
}

function cloneLogicJump(
  jump: LogicJump,
  questionIdMap: Map<string, string>,
  groupIdMap: Map<string, string>,
  order: number,
): LogicJump {
  return {
    ...structuredClone(jump),
    id: crypto.randomUUID(),
    questionId: questionIdMap.get(jump.questionId) ?? jump.questionId,
    targetQuestionId:
      jump.targetQuestionId === null
        ? null
        : (questionIdMap.get(jump.targetQuestionId) ?? jump.targetQuestionId),
    targetGroupId: jump.targetGroupId
      ? (groupIdMap.get(jump.targetGroupId) ?? jump.targetGroupId)
      : null,
    displayTargets: jump.displayTargets?.map((target) => {
      if (target.type === 'group') {
        return {
          ...target,
          id: `group:${groupIdMap.get(target.groupId) ?? target.groupId}`,
          groupId: groupIdMap.get(target.groupId) ?? target.groupId,
        }
      }
      if (target.type === 'group_question') {
        const groupId = groupIdMap.get(target.groupId) ?? target.groupId
        const questionId = questionIdMap.get(target.questionId) ?? target.questionId
        return {
          ...target,
          id: `group_question:${groupId}:${questionId}`,
          groupId,
          questionId,
        }
      }
      const questionId = questionIdMap.get(target.questionId) ?? target.questionId
      return {
        ...target,
        id: `question:${questionId}`,
        questionId,
        groupId: target.groupId ? (groupIdMap.get(target.groupId) ?? target.groupId) : null,
      }
    }),
    conditions: jump.conditions.map((condition) => ({
      ...condition,
      field: questionIdMap.get(condition.field) ?? condition.field,
    })),
    order,
  }
}

function createFormShareSettings(form: Form) {
  const publicUrl = buildPublicFormUrl(form.id)
  return {
    formId: form.id,
    publicUrl,
    isEnabled: form.status === 'published',
    closeAt: typeof form.settings.closedAt === 'string' ? form.settings.closedAt : null,
    maxResponses:
      typeof form.settings.maxResponses === 'number'
        ? form.settings.maxResponses
        : DEFAULT_SHARE_MAX_RESPONSES,
    allowRepeat: form.settings.allowResubmit === true,
    defaultSalesCode: 'ARIEL',
    defaultCampaignCode: 'Q2-NURTURE',
    hiddenFields: {
      salesCode: 'ARIEL',
      campaignCode: 'Q2-NURTURE',
    },
    qrCodeUrl: `${publicUrl}?qr=1`,
    sourceMeta: createSyntheticMeta(),
  }
}

export async function listMockFormFolders(): Promise<FormFolderListResult> {
  const state = loadFixture()
  return {
    data: [...state.formFolders].sort((left, right) => left.orderIndex - right.orderIndex),
    meta: createSyntheticMeta(),
  }
}

export function getMockWorkspaceId() {
  return mockWorkspaceId
}

export function getPersistentFormListState() {
  const state = loadFixture()
  return {
    formOrder: state.formOrder,
    deletedFormIds: state.deletedFormIds,
    formFolders: state.formFolders,
  }
}

export function saveMockFormFolders(formFolders: FormFolderContract[]) {
  return savePatch({
    formFolders: formFolders.map((folder, orderIndex) => ({ ...folder, orderIndex })),
  }).formFolders
}

export function saveMockFormListState(patch: {
  formOrder?: string[]
  deletedFormIds?: string[]
  formFolders?: FormFolderContract[]
}) {
  return savePatch((current) => ({
    formOrder: patch.formOrder ?? current.formOrder,
    deletedFormIds: patch.deletedFormIds ?? current.deletedFormIds,
    formFolders: patch.formFolders
      ? patch.formFolders.map((folder, orderIndex) => ({ ...folder, orderIndex }))
      : current.formFolders,
  }))
}

export function updateMockFormStatus(formId: string, status: FormStatus) {
  const nextUpdatedAt = new Date().toISOString()
  const updated = savePatch((current) => ({
    forms: current.forms.map((form) =>
      form.id === formId ? { ...form, status, updatedAt: nextUpdatedAt } : form,
    ),
    shareSettings: {
      ...current.shareSettings,
      ...(current.shareSettings[formId]
        ? {
            [formId]: {
              ...current.shareSettings[formId],
              isEnabled: status === 'published',
            },
          }
        : {}),
    },
  })).forms.find((form) => form.id === formId)

  return updated ?? null
}

export async function getFillableMockForm(formId: string): Promise<FormDetailContract | null> {
  const form = getPersistedFormOrNull(formId)
  if (!form || form.status !== 'published') return null
  return getMockFormDetail(form.id)
}

export async function duplicateMockForm(formId: string): Promise<FormDetailContract> {
  const state = loadFixture()
  const sourceForm = state.forms.find((form) => form.id === formId) ?? state.forms[0]
  const sourceDetail = state.formDetails[sourceForm.id] ?? {
    questions: [],
    logicJumps: [],
    hiddenFields: {},
    groups: [],
    outline: [],
  }
  const now = new Date().toISOString()
  const nextFormId = crypto.randomUUID()
  const nextForm: Form = {
    ...structuredClone(sourceForm),
    id: nextFormId,
    title: `${sourceForm.title}（複本）`,
    status: 'draft',
    updatedAt: now,
  }
  const nextDetail = cloneFormDetailForDuplicate(sourceForm.id, nextFormId, sourceDetail)
  const normalizedGroups = normalizeFormGroupInstances(nextFormId, nextDetail.groups)
  const normalizedDetail: PersistentFormDetailSnapshot = {
    ...nextDetail,
    groups: normalizedGroups,
    outline: nextDetail.outline.length
      ? nextDetail.outline
      : createQuestionOutline(nextDetail.questions, normalizedGroups),
  }

  savePatch((current) => ({
    forms: [nextForm, ...current.forms],
    formOrder: [nextForm.id, ...current.formOrder.filter((id) => id !== nextForm.id)],
    formDetails: {
      ...current.formDetails,
      [nextForm.id]: normalizedDetail,
    },
    questionGroupInstances: {
      ...current.questionGroupInstances,
      [nextForm.id]: normalizedGroups,
    },
    questionOutlineOrder: {
      ...current.questionOutlineOrder,
      [nextForm.id]: normalizedDetail.outline,
    },
    shareSettings: {
      ...current.shareSettings,
      [nextForm.id]: createFormShareSettings(nextForm),
    },
    emailShareTemplates: {
      ...current.emailShareTemplates,
      [nextForm.id]: [],
    },
  }))

  return getMockFormDetail(nextForm.id)
}

export function saveMockFormDetailSnapshot(snapshot: {
  form: Form
  questions: Question[]
  logicJumps: LogicJump[]
  groups: QuestionGroupUiContract[]
  outline?: PersistentFormDetailSnapshot['outline']
}) {
  const groups = normalizeFormGroupInstances(snapshot.form.id, snapshot.groups)
  const detail: PersistentFormDetailSnapshot = {
    questions: snapshot.questions,
    logicJumps: snapshot.logicJumps,
    hiddenFields: loadFixture().formDetails[snapshot.form.id]?.hiddenFields ?? {},
    groups,
    outline: snapshot.outline ?? createQuestionOutline(snapshot.questions, groups),
  }

  savePatch((current) => {
    const exists = current.forms.some((form) => form.id === snapshot.form.id)
    const nextForm = { ...snapshot.form, updatedAt: new Date().toISOString() }
    return {
      forms: exists
        ? current.forms.map((form) => (form.id === snapshot.form.id ? nextForm : form))
        : [nextForm, ...current.forms],
    }
  })
  return saveFormDetailSnapshot(snapshot.form.id, detail)
}

export async function listMockForms(params: ListFormsParams = {}): Promise<ListFormsResult> {
  const parsed = ListFormsParamsSchema.parse(params)
  const keyword = parsed.keyword?.trim().toLocaleLowerCase()
  const state = loadFixture()
  const deleted = new Set(state.deletedFormIds)
  const allForms = state.forms.filter((form) => !deleted.has(form.id)).map(toFormUiContract)
  const filtered = allForms.filter((form) => {
    const matchesWorkspace = !parsed.workspaceId || form.workspaceId === parsed.workspaceId
    const matchesStatus = parsed.status === 'all' || form.status === parsed.status
    const matchesFolder = !parsed.folderId || form.folderIds.includes(parsed.folderId)
    const matchesKeyword =
      !keyword ||
      [form.title, form.description, form.status].some((value) =>
        value.toLocaleLowerCase().includes(keyword),
      )
    return matchesWorkspace && matchesStatus && matchesFolder && matchesKeyword
  })
  const sorted = sortForms(filtered, parsed.sort)
  return {
    data: paginate(sorted, parsed.page, parsed.limit),
    meta: {
      ...createSyntheticMeta(),
      total: filtered.length,
      page: parsed.page,
      limit: parsed.limit,
    },
  }
}

export async function getMockFormDetail(formId: string): Promise<FormDetailContract> {
  const form = getPersistedForm(formId)
  const state = loadFixture()
  const detail = state.formDetails[form.id] ?? {
    questions: [],
    logicJumps: [],
    hiddenFields: {},
    groups: [],
    outline: [],
  }
  const groups =
    state.questionGroupInstances[form.id] ?? normalizeFormGroupInstances(form.id, detail.groups)
  const outline =
    state.questionOutlineOrder[form.id] ??
    detail.outline ??
    createQuestionOutline(detail.questions, groups)
  const responses = state.responses.filter((response) => response.formId === form.id)
  const completions = responses.filter((response) => response.status === 'complete').length
  return {
    ...form,
    questions: detail.questions,
    logicJumps: detail.logicJumps,
    hiddenFields: detail.hiddenFields,
    groups,
    outline,
    responseCount: responses.length,
    completionRate: responses.length === 0 ? 0 : Math.round((completions / responses.length) * 100),
    sourceMeta: createSyntheticMeta(),
  }
}

export async function getMockFormPreviewSnapshot(
  formId: string,
  source: Extract<FormPreviewSource, 'persistent_mock' | 'public_preview'> = 'persistent_mock',
): Promise<FormPreviewSnapshot> {
  const detail = await getMockFormDetail(formId)
  return normalizeFormPreviewSnapshot({
    source,
    mode: 'full_flow',
    form: detail,
    questions: detail.questions,
    groups: detail.groups,
    outline: detail.outline,
    logicJumps: detail.logicJumps,
    hiddenFields: detail.hiddenFields,
    sourceMeta: detail.sourceMeta,
  })
}

export async function previewMockFormPublish(formId: string) {
  const form = getPersistedForm(formId)
  const nextUpdatedAt = new Date().toISOString()
  savePatch((current) => ({
    forms: current.forms.map((item) =>
      item.id === form.id ? { ...item, status: 'published', updatedAt: nextUpdatedAt } : item,
    ),
  }))
  return {
    formId: form.id,
    status: 'published' as const,
    nextUpdatedAt,
    sourceMeta: createSyntheticMeta(),
  }
}
